import { useState } from 'react';
import { guideTemplates } from '../../../data/guideTemplates';

interface UserGuideProps {
    onLoadTemplate?: (template: any, name: string) => void;
}

type NodeCategory = 'data' | 'transform' | 'combine' | 'trading' | 'variance' | 'ai' | 'monitor';

interface NodeDetail {
    title: string;
    description: string;
    nodes: string[];
    technicalDesc: string;
    useCase: string;
}

const nodeDetails: Record<NodeCategory, NodeDetail> = {
    data: {
        title: 'Data Nodes',
        description: 'These nodes represent the raw data input for your tree. They are the primary source of truth for any model.',
        nodes: ['Price VWAP', 'Traded volume', 'Temperature', 'Power production', 'Gas Linepack', 'Orderbook Snapshots'],
        technicalDesc: 'Data nodes act as the terminal "leaves" of the graph. They fetch historical time-series data from internal registries or external providers.',
        useCase: 'Fetch a historical price series for "TTF Front Month" or analyze the correlation between price and volume.'
    },
    transform: {
        title: 'Transform Nodes',
        description: 'Single-input operators that modify the shape or perform complex calculations on the data.',
        nodes: ['Mean', 'Moving average cross', 'Single moving average cross', 'Slope of moving average', 'Trendlines', 'Standard deviation', 'Regime Classifier', 'Z-Score', 'RSI'],
        technicalDesc: 'Accepts exactly one child node. It processes every data point in the time series through a specific function (e.g. moving average) before passing it up.',
        useCase: 'Calculating the moving average of a price series to find entry points for a trade, or classifying market regimes (trending vs mean-reverting).'
    },
    combine: {
        title: 'Combine Nodes',
        description: 'Multi-input operators that define relationships between different sources of data.',
        nodes: ['Arithmetic Operators (+, -, *, /)', 'Comparisons', 'Filter (if)', 'Rolling Correlation (corr)'],
        technicalDesc: 'Accepts two or more child nodes. It aligns the timestamps of the children and performs set operations, arithmetic, or rolling statistics between them.',
        useCase: 'Calculating the spread (Subtraction) between TTF and NBP, or monitoring when the 20-day correlation breaks down.'
    },
    trading: {
        title: 'Strategy PnL',
        description: 'Estimate the hypothetical performance of your entry and exit signals.',
        nodes: ['Long Mode', 'Short Mode', 'Swap Signals', 'Max Lots Enforcement'],
        technicalDesc: 'The PnL node takes an "Entry" and an "Exit" signal. It matches entries to the next available exit (forward-in-time) and calculates the resulting PnL based on the direction (Long/Short).',
        useCase: 'Testing how a "Moving Average Cross" entry performs when exited by a "Price Spike" filter.'
    },
    variance: {
        title: 'Variance & Attribution',
        description: 'Multi-factor attribution and correlation analysis to identify primary market drivers.',
        nodes: ['Target Node', 'Parameter Nodes', 'Attribution Scores', 'Correlation Matrix (Heatmap)'],
        technicalDesc: 'The Variance node performs multi-variable analysis. It requires one "Target" series and up to 3 "Parameters". It calculates attribution (Random Forest) and a Pearson correlation matrix of deltas.',
        useCase: 'Set "Clean Spark Spread" as Target and "Gas Inventory" + "Wind" as Parameters. Use the Heatmap to ensure your parameters aren\'t just measuring the same market driver (multi-collinearity).'
    },
    ai: {
        title: 'AI & Insight',
        description: 'Context-aware interpretation and statistical attribution of your strategy results.',
        nodes: ['Factor Attribution', 'Structural Edge', 'Intraday Cyclicality', 'Predictive Power', 'Backtest Narratives'],
        technicalDesc: 'Our AI engine automatically analyzes datasets. It translates statistical volatility and complex relationships into actionable trading advice and human-readable narratives.',
        useCase: 'Generating a summary report for a complex backtest to identify anomalous regime shifts or structural edges.'
    },
    monitor: {
        title: 'Monitoring',
        description: 'Persistent background execution of your strategies with alert delivery.',
        nodes: ['Condition Mapping', 'Teams Integration', 'Update Workflow', '30-day Relevance Scanning'],
        technicalDesc: 'Monitors use presence-based triggering with statistical suppression. If your logic produces output that passes the 30-day relevance filter, an alert is dispatched.',
        useCase: 'Deploying a Price-Spike filter to run every hour and notify you only when the signal is statistically significant relative to the last month.'
    }
};

export function UserGuide({ onLoadTemplate }: UserGuideProps) {
    const [selectedCategory, setSelectedCategory] = useState<NodeCategory>('data');

    return (
        <div style={{
            height: '100%',
            overflowY: 'auto',
            background: 'var(--bg-app)',
            color: 'var(--text-primary)',
            padding: '2rem',
            fontFamily: 'Inter, system-ui, sans-serif',
            lineHeight: '1.5'
        }}>
            <div style={{ maxWidth: '1800px', margin: '0 auto' }}>

                {/* Main Content Layout - 3 Columns */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1.2fr 1fr',
                    gap: '2rem',
                    alignItems: 'start'
                }}>

                    {/* Column 1: Methodology header + Tree */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

                        <header>
                            <h1 style={{
                                fontSize: '2.5rem',
                                fontWeight: '800',
                                marginBottom: '0.8rem',
                                background: 'linear-gradient(to right, var(--accent-primary), var(--node-combine), var(--chart-series-5))',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}>
                                MoneyMaker Academy
                            </h1>
                            <p style={{
                                fontSize: '1.1rem',
                                color: 'var(--text-muted)',
                                fontWeight: '400',
                            }}>
                                MoneyMaker is designed for <strong>Hypothesis Testing</strong>.
                                Model complex market relationships by building visual relationship trees.
                            </p>
                        </header>

                        <section style={{
                            background: 'var(--surface-glass)',
                            borderRadius: '24px',
                            padding: '2rem',
                            border: '1px solid var(--surface-glass-border)',
                        }}>
                            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                🌿 The Relationship Tree
                            </h2>
                            <p style={{ fontSize: '1rem', marginBottom: '1.5rem' }}>
                                The system uses a <strong>recursive logic model</strong>.
                                Instead of static formulas, you build a tree where each node requests data from its children to produce a result.
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                                <div style={{ padding: '0.8rem', background: 'var(--accent-glow)', borderRadius: '12px', borderLeft: '3px solid var(--accent-primary)', fontSize: '0.9rem' }}>
                                    <strong>The Root Node:</strong> Your final output. It is the result of the entire tree logic (e.g., "Hub Correlation").
                                </div>
                                <div style={{ padding: '0.8rem', background: 'var(--surface-overlay-subtle)', borderRadius: '12px', borderLeft: '3px solid var(--node-combine)', fontSize: '0.9rem' }}>
                                    <strong>The Branches:</strong> Relationship nodes (Combine/Transform) that process data as it flows upwards.
                                </div>
                                <div style={{ padding: '0.8rem', background: 'var(--accent-success-subtle)', borderRadius: '12px', borderLeft: '3px solid var(--accent-success)', fontSize: '0.9rem' }}>
                                    <strong>The Leaves:</strong> The raw inputs. These fetch the actual price or volume data from the market.
                                </div>
                            </div>
                        </section>

                        <section style={{
                            background: 'var(--surface-glass)',
                            borderRadius: '24px',
                            padding: '2rem',
                            border: '1px solid var(--surface-glass-border)',
                        }}>
                            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.2rem' }}>
                                �️ Strategy Updates
                            </h2>
                            <p style={{ fontSize: '0.95rem', color: 'var(--text-muted)' }}>
                                Monitored strategies can be modified directly from the builder. Load a monitor, adjust the logic, and right-click any node to select <strong>Update Monitoring</strong> to sync changes to the live runner.
                            </p>
                        </section>
                    </div>

                    {/* Column 2: Node Reference */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                        <h2 style={{ fontSize: '1.8rem', marginBottom: '0rem', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                            ⚙️ Academy Reference
                        </h2>

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                            height: '100%'
                        }}>
                            <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
                                {(Object.keys(nodeDetails) as NodeCategory[]).map(cat => (
                                    <div
                                        key={cat}
                                        onClick={() => setSelectedCategory(cat)}
                                        style={{
                                            padding: '0.8rem 1.2rem',
                                            background: selectedCategory === cat ? 'var(--accent-glow)' : 'var(--bg-card)',
                                            border: `1px solid ${selectedCategory === cat ? 'var(--accent-primary)' : 'var(--surface-glass-border)'}`,
                                            borderRadius: '10px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.6rem',
                                            justifyContent: 'center',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        <div style={{
                                            width: '6px', height: '6px', borderRadius: '50%',
                                            background: selectedCategory === cat ? 'var(--accent-primary)' : 'var(--text-muted)'
                                        }} />
                                        <span style={{ fontWeight: '600' }}>{nodeDetails[cat].title}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Detail Panel */}
                            <div style={{
                                background: 'var(--surface-glass-highlight)',
                                borderRadius: '24px',
                                padding: '2rem',
                                border: '1px solid var(--surface-glass-border)',
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <h3 style={{ fontSize: '1.6rem', marginBottom: '0.8rem', color: 'var(--accent-primary)' }}>
                                    {nodeDetails[selectedCategory].title}
                                </h3>
                                <p style={{ fontSize: '1rem', marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                                    {nodeDetails[selectedCategory].description}
                                </p>

                                <div style={{ marginBottom: '2rem' }}>
                                    <h4 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '0.8rem' }}>Technical Options</h4>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                                        {nodeDetails[selectedCategory].nodes.map(node => (
                                            <span key={node} style={{
                                                padding: '6px 12px',
                                                background: 'var(--bg-app)',
                                                borderRadius: '16px',
                                                fontSize: '0.85rem',
                                                border: '1px solid var(--surface-glass-border)'
                                            }}>
                                                {node}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                    <div>
                                        <h4 style={{ fontWeight: 'bold', fontSize: '1rem', marginBottom: '0.4rem' }}>How it Behaves</h4>
                                        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: '1.6' }}>{nodeDetails[selectedCategory].technicalDesc}</p>
                                    </div>
                                    <div style={{
                                        padding: '1.2rem',
                                        background: 'var(--accent-glow)',
                                        borderRadius: '14px',
                                        border: '1px solid var(--accent-primary)',
                                        fontSize: '0.95rem'
                                    }}>
                                        <strong style={{ display: 'block', marginBottom: '0.4rem', color: 'var(--accent-primary)' }}>Practical Example</strong>
                                        {nodeDetails[selectedCategory].useCase}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Column 3: Philosophy */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', paddingTop: '4rem' }}> {/* Adjusted padding to align with other column content starts */}
                        <section style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div style={{ padding: '1.5rem', background: 'var(--bg-panel)', borderRadius: '20px', border: '1px solid var(--surface-glass-border)' }}>
                                <h3 style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Test, don't just calculate</h3>
                                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                                    The goal is to validate market assumptions. MoneyMaker focuses on historical relationship verification, not transactional simulations.
                                </p>
                            </div>
                            <div style={{ padding: '1.5rem', background: 'var(--accent-success-subtle)', borderRadius: '20px', border: '1px solid var(--accent-success)' }}>
                                <h3 style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Relationship Mapping</h3>
                                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                                    Answer fundamental questions: <em>"Does this spread mean-revert?"</em> or
                                    <em>"Is the correlation between gas and power stronger in specific seasons?"</em>
                                </p>
                            </div>
                        </section>

                        {/* Quick Controls Section (Moved to Col 3) */}
                        <section style={{
                            marginTop: '0.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.8rem',
                            paddingTop: '1rem',
                            borderTop: '1px dashed var(--surface-glass-border)'
                        }}>
                            <h3 style={{
                                fontSize: '0.9rem',
                                color: 'var(--text-muted)',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                marginBottom: '0.2rem'
                            }}>
                                ⚡ Quick Controls
                            </h3>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', background: 'var(--surface-glass-highlight)', padding: '0.6rem', borderRadius: '8px' }}>
                                <div style={{ fontSize: '1.2rem' }}>🖱️</div>
                                <div><strong style={{ fontSize: '0.9rem' }}>Add Nodes</strong> <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>- Drag or click</span></div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', background: 'var(--surface-glass-highlight)', padding: '0.6rem', borderRadius: '8px' }}>
                                <div style={{ fontSize: '1.2rem' }}>✍️</div>
                                <div><strong style={{ fontSize: '0.9rem' }}>Configure</strong> <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>- Select values</span></div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', background: 'var(--surface-glass-highlight)', padding: '0.6rem', borderRadius: '8px' }}>
                                <div style={{ fontSize: '1.2rem' }}>⚙️</div>
                                <div><strong style={{ fontSize: '0.9rem' }}>Advanced</strong> <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>- Context menu</span></div>
                            </div>
                        </section>
                    </div>

                </div>



                {/* Footer: Templates */}
                <section style={{
                    marginTop: '3rem',
                    textAlign: 'center',
                    padding: '2rem',
                    background: 'var(--bg-card)',
                    borderRadius: '24px',
                    border: '1px solid var(--surface-glass-border)'
                }}>
                    <h2 style={{ fontSize: '1.8rem', marginBottom: '0.8rem' }}>Ready to build?</h2>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 1.5rem auto', fontSize: '0.95rem' }}>
                        Use the <strong>Build</strong> tab to start modeling from scratch, or choose an example below to see a structured relationship in action.
                    </p>

                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {guideTemplates.map(t => (
                            <button
                                key={t.id}
                                onClick={() => onLoadTemplate?.(t.nodeStructure, t.title)}
                                style={{
                                    padding: '0.8rem 1.6rem',
                                    background: 'var(--bg-app)',
                                    border: '1px solid var(--surface-glass-border)',
                                    borderRadius: '12px',
                                    color: 'var(--text-primary)',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '0.95rem',
                                    transition: 'all 0.2s ease',
                                    boxShadow: 'var(--shadow-sm)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'var(--surface-overlay-subtle)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'var(--bg-app)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                {t.id === 'clean-spark-spread' ? 'Core Methodology' : t.title}
                            </button>
                        ))}
                    </div>
                </section>

            </div>
        </div>
    );
}

